import {ActionIcon, Button, Checkbox, Divider, Group, Menu, Modal, ModalProps, Stack, Title} from "@mantine/core";
import {
  ServiceTypeFacetModeDataTable
} from "../../data-table/ServiceTypeFacetModeDataTable/ServiceTypeFacetModeDataTable";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {
  CompanyEntity,
  getObjectId,
  ServiceTypeEntity,
  serviceTypeFacetModesSelector,
  useGetAllServiceTypeFacetModesQuery
} from "@ots-frontend/ots-data";
import {Card, CardProps} from "../Card/Card";
import {IconPlus} from "@tabler/icons-react";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {useDisclosure} from "@mantine/hooks";
import {useServiceTypeFacetModeMutation} from "../../../hooks/useServiceTypeFacetModeMutation";
import {useForm} from "@mantine/form";
import {RefServiceModeSelect} from "../../select/entity/RefServiceModeSelect/RefServiceModeSelect";
import {RefServiceStatusFacetSelect} from "../../select/entity/RefServiceStatusFacetSelect/RefServiceStatusFacetSelect";

export type ServiceTypeFacetModeDataTableCardProps = CardProps & {
  company: CompanyEntity | string
  serviceType: ServiceTypeEntity | string
}

export const ServiceTypeFacetModeDataTableCard = (props: ServiceTypeFacetModeDataTableCardProps) => {
  const {
    company,
    serviceType,
    shadow = "sm",
    padding = "lg",
    radius = "md",
    withBorder = true,
    ...rest
  } = props

  const {t} = useTranslation(["common"])

  const [modalOpened, modalHandler] = useDisclosure(false);

  const selectServiceTypeFacetModes = useMemo(() => {
    return serviceTypeFacetModesSelector();
  }, []);

  const {
    serviceTypeFacetModes,
    isServiceTypeFacetModesLoading
  } = useGetAllServiceTypeFacetModesQuery({
    companyId: getObjectId(company),
    serviceTypeId: getObjectId(serviceType),
  }, {
    selectFromResult: (result) => ({
      serviceTypeFacetModes: selectServiceTypeFacetModes(result, {}),
      isServiceTypeFacetModesLoading: result.isLoading
    })
  });

  const {
    createServiceTypeFacetMode,
    isCreating,
  } = useServiceTypeFacetModeMutation()

  const onNewClick = useCallback(() => {
    modalHandler.open();
  }, [modalHandler])

  const onSubmit = useCallback((value: { facet: string, mode: string, primary?: boolean } | undefined) => {
    if (value) {
      createServiceTypeFacetMode({
        companyId: getObjectId(company),
        serviceTypeId: getObjectId(serviceType),
        facetCode: value.facet,
        modeCode: value.mode,
        primary: value.primary ?? false,
      }, {
        onSuccess: () => modalHandler.close()
      })
    }
  }, [createServiceTypeFacetMode, company, serviceType, modalHandler])

  return (
    <Card shadow={shadow} padding={padding} radius={radius} withBorder={withBorder} {...rest}>
      <Group position="apart" pb="sm">
        <Title order={4}>{t("common.entity.servicetypefacetmode.plural", "Service Type Facet Modes")}</Title>
        <Group spacing={4} position="left" noWrap>
          <HasAccess permissions={["servicetype_edit"]}>
            <Menu
              width={260}
              position="bottom-end"
              transitionProps={{transition: "pop-top-right"}}
              withinPortal
            >
              <Menu.Target>
                <ActionIcon color="green">
                  <IconPlus size={16}/>
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={onNewClick}>
                  {t("common.actions.new", "New") + " " + t("common.entity.servicetypefacetmode", "Service Type Facet Mode")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </HasAccess>
        </Group>
      </Group>

      <Divider pb="sm"/>

      <ServiceTypeFacetModeDataTable
        withBorder
        records={serviceTypeFacetModes}
        fetching={isServiceTypeFacetModesLoading || isCreating}
      />

      <ServiceTypeFacetModeModal
        opened={modalOpened}
        onClose={modalHandler.close}
        title={t("common.entity.servicetypefacetmode.plural", "Service Type Facet Modes")}
        onSubmit={onSubmit}
      />

    </Card>
  )
}

interface ServiceTypeFacetModeModalProps extends Omit<ModalProps, "onSubmit"> {
  onSubmit?: (value: { facet: string, mode: string, primary?: boolean } | undefined) => void
}

const ServiceTypeFacetModeModal = (props: ServiceTypeFacetModeModalProps) => {
  const {
    onSubmit,
    onClose,
    ...rest
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: {
      facet: undefined,
      mode: undefined,
      primary: false,
    },
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        facet: values.facet!,
        mode: values.mode!,
        primary: values.primary ?? false
      })
      form.reset()
    }
  }, [form, onSubmit]);

  const _onClose = useCallback(() => {
    onClose?.()
    form.reset()
  }, [onClose, form])

  return (
    <Modal {...rest} onClose={_onClose}>
      <form onSubmit={form.onSubmit(onFormSubmit)}>
        <Stack spacing="md">
          <RefServiceStatusFacetSelect
            label={t("common.entity.servicetypefacet.form.field.facet", "Facet")}
            placeholder={t<string>("common.entity.servicetypefacet.form.field.facet", "Facet")}
            pt="md"
            withinPortal={true}
            clearable
            withAsterisk
            required
            {...form.getInputProps("facet")} />
          <RefServiceModeSelect
            label={t("common.entity.servicetypefacetmode.form.field.mode", "Mode")}
            placeholder={t<string>("common.entity.servicetypefacetmode.form.field.mode", "Mode")}
            pt="md"
            withinPortal={true}
            clearable
            withAsterisk
            required
            {...form.getInputProps("mode")} />
          <Divider my="sm" variant="dotted"/>
          <Checkbox
            label={t("common.entity.servicetypefacetmode.form.field.primary", "Primary")}
            {...form.getInputProps("primary", {type: "checkbox"})}
            mb="sm"
          />
          <Button type="submit" fullWidth mt="xl">
            {t("common.action.submit", {ns: "common"})}
          </Button>
        </Stack>
      </form>
    </Modal>
  );
};
